// Settings shape — globally-shared colour palettes, served via
// GET /api/settings and replaced wholesale via PUT /api/settings.
// Persisted in `.data.sqlite` (port_colors + event_kind_colors tables)
// — see `backend/palette.ts`. Per-machine UI prefs used to live here
// too (panelHeight, defaultPorts) but were removed once the
// per-profile workbench moved into `current_views` and the bottom
// details panel was retired in favour of the modal.

export interface Settings {
  // Per-terminal colour overrides — { [portName]: "#hex" }.
  portColors: Record<string, string>;
  // Per-kind colour for date-events background bands on the calendar
  // ({ [kindName]: "#hex" }). User can also create kinds on the fly when
  // adding an event; the new kind lands here with a default colour the
  // user can later tweak.
  eventKindColors: Record<string, string>;
}

export const DEFAULT_EVENT_KIND_COLORS: Record<string, string> = {
  Holiday: "#f59e0b",       // amber — matches the original holiday wash
  Blockage: "#ef4444",      // red
  "Road works": "#f97316",  // orange
  Construction: "#8b5cf6",  // violet
  Other: "#94a3b8",         // slate (fallback)
};

export const DEFAULT_SETTINGS: Settings = {
  portColors: {},
  eventKindColors: { ...DEFAULT_EVENT_KIND_COLORS },
};

// Strict shape gate for PUT /api/settings. parseSettings coerces
// anything to a valid Settings — which turns a malformed payload into
// a silent wipe of the shared palette. Reject those with a 400 instead.
export function isSettingsShape(raw: unknown): boolean {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  const r = raw as Record<string, unknown>;
  const isPlainObject = (v: unknown) => !!v && typeof v === "object" && !Array.isArray(v);
  return isPlainObject(r.portColors) && isPlainObject(r.eventKindColors);
}

// Defensive parser — coerces missing or wrong-typed fields back to
// the default so a malformed PUT payload doesn't blow up the store.
export function parseSettings(raw: unknown): Settings {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_SETTINGS, eventKindColors: { ...DEFAULT_EVENT_KIND_COLORS } };
  const r = raw as Record<string, unknown>;
  const out: Settings = { ...DEFAULT_SETTINGS, eventKindColors: { ...DEFAULT_EVENT_KIND_COLORS } };
  if (r.portColors && typeof r.portColors === "object" && !Array.isArray(r.portColors)) {
    const pc: Record<string, string> = {};
    for (const [k, v] of Object.entries(r.portColors as Record<string, unknown>)) {
      if (typeof v === "string") pc[k] = v;
    }
    out.portColors = pc;
  }
  if (r.eventKindColors && typeof r.eventKindColors === "object" && !Array.isArray(r.eventKindColors)) {
    const ekc: Record<string, string> = { ...DEFAULT_EVENT_KIND_COLORS };
    for (const [k, v] of Object.entries(r.eventKindColors as Record<string, unknown>)) {
      if (typeof v === "string") ekc[k] = v;
    }
    out.eventKindColors = ekc;
  }
  return out;
}

// The wire shape for a lane list (view template / current view): an
// array of non-empty strings. Shared by current-views and view-templates
// so the two validators can't drift.
export function isValidPorts(ports: unknown): ports is string[] {
  return Array.isArray(ports) && ports.every((p) => typeof p === "string" && p.length > 0);
}

// The stored colour is interpolated into inline styles, so accept only
// real 3- or 6-digit hex, not arbitrary text.
export function isHexColour(v: unknown): v is string {
  return typeof v === "string" && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(v);
}

// Every non-hex colour entry across both maps, as "map.key=value". Empty
// array = all valid. The PUT /api/settings handler rejects a non-empty
// result with 400 rather than letting the store silently drop the bad
// entries (a partial persist that reads as a wipe).
export function settingsHexErrors(raw: unknown): string[] {
  if (!raw || typeof raw !== "object") return [];
  const r = raw as Record<string, unknown>;
  const errs: string[] = [];
  for (const mapKey of ["portColors", "eventKindColors"] as const) {
    const m = r[mapKey];
    if (m && typeof m === "object" && !Array.isArray(m)) {
      for (const [k, v] of Object.entries(m as Record<string, unknown>)) {
        if (!isHexColour(v)) errs.push(`${mapKey}.${k}=${JSON.stringify(v)}`);
      }
    }
  }
  return errs;
}

// Custom date event — user-defined band on the calendar timeline. Holidays
// from /api/holidays follow the same shape but are sourced separately and
// flow through the same render path on the frontend; only events created
// by the user are persisted in this list.
// Date event scope: country is required, port + terminal cascade and
// are optional. A country-only event applies to the whole country; a
// country+port event applies to all terminals at that port; a full
// triple narrows it to a single terminal. Empty string is the wire
// representation for "not set" at the port and terminal levels.
//
// `source` distinguishes user-created rows from those synced in from
// the public-holidays API: holiday rows are read-only in the UI and
// get re-written on every sync.
export type DateEventSource = "user" | "holiday";

export interface DateEvent {
  // Stable monotonically-incrementing id from the backend SQLite store —
  // exposed so the frontend can edit / delete a specific row.
  id: number;
  source: DateEventSource;
  country: string;        // ISO 3166-1 alpha-2; required for new events
  portGroup: string;      // port-group name (e.g. "ANTWERP"); "" = country-wide
  portName: string;       // exact catalogue terminal name; "" = port-wide
  startDate: string;      // YYYY-MM-DD (local to the port)
  endDate: string;        // YYYY-MM-DD (inclusive)
  // Optional clock-time window inside the date span. HH:MM 24h (00:00–23:59)
  // or null. Either may be null independently: null startTime = day starts
  // at 00:00 on startDate; null endTime = day ends at 24:00 (next-day
  // midnight) on endDate. Both null = full-day (default). When both are set
  // on a single-day event, startTime must be strictly less than endTime.
  startTime: string | null;
  endTime: string | null;
  kind: string;           // free-text; lookup in Settings.eventKindColors
  description: string;    // optional; "" if none
}

// Per-stop user overrides for one voyage stop. Stevedore window =
// when crew actually works the cargo (typically inside but narrower
// than the arrival/departure span). Volume overrides override the
// upstream cargo totals when the user has more accurate numbers
// (e.g. agreed plan vs estimate). All fields are optional — null
// means "no override, use the upstream/default value".
//
// Volume overrides come in two shapes:
//   - loadUnits / dischargeUnits / shiftingUnits — legacy single
//     totals, kept for backward-compat with rows stored before the
//     per-segment breakdown landed.
//   - *Segments — { C, HH, RT, BB, NCC } maps with per-segment
//     unit counts. New rows write into the segment maps; readers
//     prefer the segment values when present.
export interface SegmentUnits {
  C?:   number | null;
  HH?:  number | null;
  RT?:  number | null;
  BB?:  number | null;
  NCC?: number | null;
}
// Discriminator that disambiguates a real stop from an upstream
// "dummy" anchor borrowed from a neighbouring voyage. Dummies REUSE
// the `order` value of one of THIS voyage's real stops, so the
// override store keys on (vesselCode, voyageNo, stopOrder, dummySource)
// rather than just stop_order — without this an edit to the real
// stop at order=N would leak onto the dummy at the same order.
// "" = real stop; "previous" / "next" = upstream's dummySource value.
export type DummySource = "" | "previous" | "next";

export interface StevedoreSession {
  start: string;  // ISO local datetime "YYYY-MM-DDTHH:mm"
  end: string;
}

export interface VoyageStopOverride {
  vesselCode: string;
  voyageNo: string;
  stopOrder: number;
  // "" for real stops, matches the itinerary stop's `dummySource` for
  // dummy anchors. Persisted as a non-null TEXT column so SQLite can
  // include it in the composite PK.
  dummySource: DummySource;
  // N stevedore working windows per stop. Replaced the legacy
  // stevedoreStart/stevedoreEnd pair (schema-migrated in overrides.ts).
  stevedoreSessions: StevedoreSession[] | null;
  loadUnits:      number | null;
  dischargeUnits: number | null;
  shiftingUnits:  number | null;
  // Optional for backward-compat — rows persisted before the segment
  // maps landed don't have these. New writers set them explicitly.
  loadSegments?:      SegmentUnits | null;
  dischargeSegments?: SegmentUnits | null;
  shiftingSegments?:  SegmentUnits | null;
  // Content-based stop identity (re-cut-stable binding). Nullable /
  // optional: legacy rows and the wire format from older clients omit
  // them. When present, the frontend resolver binds on these instead of
  // stopOrder. stopOrder stays as the storage key + last-known position.
  portExternalRef?: string | null;
  portFunc?:        string | null;
  occurrenceIndex?: number | null;
}

// Subset of cargo-segment codes the dashboard recognises. Mirrors
// the canonical display order in voyage-mapper's SEGMENT_ORDER.
const SEGMENT_CODES = ["C", "HH", "RT", "BB", "NCC"] as const;

export function parseSegmentUnits(raw: unknown): SegmentUnits | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const out: SegmentUnits = {};
  let anyValue = false;
  for (const code of SEGMENT_CODES) {
    const v = r[code];
    if (typeof v === "number" && Number.isFinite(v)) {
      out[code] = v;
      anyValue = true;
    } else if (v === null) {
      out[code] = null;
    }
  }
  return anyValue ? out : null;
}

function isoOrNull(v: unknown): string | null {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v) ? v : null;
}

export function parseStevedoreSessions(raw: unknown): StevedoreSession[] | null {
  if (!Array.isArray(raw)) return null;
  const out: StevedoreSession[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as { start?: unknown; end?: unknown };
    const start = isoOrNull(o.start);
    const end = isoOrNull(o.end);
    if (start && end) out.push({ start, end });
  }
  return out.length === 0 ? null : out;
}

export function parseVoyageStopOverride(raw: unknown): VoyageStopOverride | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const vesselCode = typeof r.vesselCode === "string" ? r.vesselCode : "";
  const voyageNo = typeof r.voyageNo === "string"
    ? r.voyageNo
    : (typeof r.voyageNo === "number" ? String(r.voyageNo) : "");
  const stopOrder = typeof r.stopOrder === "number" && Number.isFinite(r.stopOrder)
    ? Math.trunc(r.stopOrder) : NaN;
  if (!vesselCode || !voyageNo || !Number.isFinite(stopOrder)) return null;
  const numOrNull = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const dummySource: DummySource =
    r.dummySource === "previous" || r.dummySource === "next" ? r.dummySource : "";
  return {
    vesselCode,
    voyageNo,
    stopOrder,
    dummySource,
    stevedoreSessions: parseStevedoreSessions(r.stevedoreSessions),
    loadUnits:      numOrNull(r.loadUnits),
    dischargeUnits: numOrNull(r.dischargeUnits),
    shiftingUnits:  numOrNull(r.shiftingUnits),
    loadSegments:      parseSegmentUnits(r.loadSegments),
    dischargeSegments: parseSegmentUnits(r.dischargeSegments),
    shiftingSegments:  parseSegmentUnits(r.shiftingSegments),
    portExternalRef: typeof r.portExternalRef === "string" ? r.portExternalRef : null,
    portFunc:        typeof r.portFunc === "string" ? r.portFunc : null,
    occurrenceIndex: typeof r.occurrenceIndex === "number" && Number.isFinite(r.occurrenceIndex)
      ? Math.trunc(r.occurrenceIndex) : null,
  };
}

const HH_MM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Returns the canonical "HH:MM" string, null for "absent", or "invalid"
// when the caller passed a non-null value that doesn't match the format.
function parseHHMM(raw: unknown): string | null | "invalid" {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "string") return "invalid";
  return HH_MM_RE.test(raw) ? raw : "invalid";
}

export function parseDateEvent(raw: unknown): DateEvent | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === "number" ? r.id : -1;
  const source: DateEventSource = r.source === "holiday" ? "holiday" : "user";
  const country = typeof r.country === "string" ? r.country : "";
  const portGroup = typeof r.portGroup === "string" ? r.portGroup : "";
  const portName = typeof r.portName === "string" ? r.portName : "";
  const startDate = typeof r.startDate === "string" ? r.startDate : "";
  const endDate = typeof r.endDate === "string" ? r.endDate : "";
  const kind = typeof r.kind === "string" ? r.kind : "";
  const description = typeof r.description === "string" ? r.description : "";
  if (!startDate || !endDate || !kind) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return null;
  // Need at least one of {country, portName} so the event has a scope to
  // attach to. Legacy rows from before the country column existed only
  // have portName — that's fine, frontend derives the country.
  if (!country && !portName) return null;

  const startTime = parseHHMM(r.startTime);
  const endTime = parseHHMM(r.endTime);
  if (startTime === "invalid" || endTime === "invalid") return null;
  // Single-day ordering: when both times are set on the same date, the
  // window must have positive duration. Multi-day always valid because
  // the date gap covers the order.
  if (startDate === endDate && startTime && endTime && startTime >= endTime) return null;

  return {
    id, source, country, portGroup, portName,
    startDate, endDate, startTime, endTime,
    kind, description,
  };
}

// Wire/storage shape for a saved Port Calendar lane configuration.
// Owned here (not in backend/view-templates.ts) so the frontend can
// re-export the same type without having to pull in modules that
// depend on bun:sqlite.
export interface ViewTemplate {
  id: number;
  name: string;
  ports: string[];          // ordered terminal externalRef codes
  createdAt: string;        // ISO 8601 UTC
  updatedAt: string;        // ISO 8601 UTC
}

// Wire/storage shape for the per-user auto-saved lane state. Same
// reason as ViewTemplate above — kept here to be importable from
// both backend store + frontend API client.
export interface CurrentView {
  ports: string[];          // ordered terminal externalRef codes
  updatedAt: string;        // ISO 8601 UTC
}

// ---------- shared store utilities ----------

// ISO 8601 UTC "now" — used as the createdAt / updatedAt stamp on
// every store write. Pulled out so every store doesn't redefine the
// same one-liner, and so tests can mock Date once if they want
// deterministic timestamps.
// Holiday row description from a Nager payload — "Local (English)"
// unless they match. Server sync + seeder must produce byte-identical
// descriptions or the holiday-dedup unique index stops matching.
export function holidayDescription(localName: string, name: string): string {
  return localName === name ? localName : `${localName} (${name})`;
}

export function nowIso(): string {
  return new Date().toISOString();
}

// Decode a JSON-encoded string array out of a TEXT column. Returns
// the empty array on parse failure, on non-array roots, and drops
// non-string entries. Used by every store whose row carries an
// ordered list of opaque codes (port externalRefs in current-views
// and view-templates today).
//
// `onError` is called when the JSON parse itself throws or when the
// root isn't an array — without it, callers that silently recover from
// a corrupt row can't tell the difference between "user never saved
// ports" and "DB row is corrupt". Pass a logEvent wrapper from
// production callers so operators see the warning.
export function parseStringArrayJson(
  raw: string,
  onError?: (err: unknown) => void,
): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((p): p is string => typeof p === "string");
    if (onError) onError(new Error(`expected array, got ${typeof parsed}`));
  } catch (err) {
    if (onError) onError(err);
  }
  return [];
}

// SQLite raises a UNIQUE-constraint error with a recognisable message
// when an INSERT/UPDATE would violate a unique index. Matching by
// substring rather than engine-specific error codes survives
// bun:sqlite version bumps. Pass the qualified column ("table.col")
// to disambiguate when a store has more than one UNIQUE index.
export function isUniqueConstraintError(err: unknown, qualifiedColumn?: string): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  if (!msg.includes("UNIQUE constraint failed")) return false;
  return qualifiedColumn ? msg.includes(qualifiedColumn) : true;
}
